// Device breakpoints configuration
export const DEVICE_BREAKPOINTS = {
  MOBILE: 390, // mobile mockup width
  TABLET: 768,
  DESKTOP: 1440, // desktop mockup width
  LARGE_DESKTOP: 2560,
} as const;

// Base font size (px)
const BASE_FONT_SIZE = 16;

/**
 * Calculate the ratio between current screen width and mockup width
 */
const calculateScreenRatio = (): number => {
  const width = document.documentElement.clientWidth;
  const base =
    width < DEVICE_BREAKPOINTS.TABLET
      ? DEVICE_BREAKPOINTS.MOBILE
      : DEVICE_BREAKPOINTS.DESKTOP;

  // Keep 1:1 ratio for desktop within reasonable range
  if (
    width >= DEVICE_BREAKPOINTS.DESKTOP &&
    width <= DEVICE_BREAKPOINTS.LARGE_DESKTOP
  ) {
    return 1;
  }

  return Math.min(width / base, 2);
};

const updateRootFontSize = (): void => {
  const ratio = calculateScreenRatio();
  document.documentElement.style.fontSize = `${BASE_FONT_SIZE * ratio}px`;
};

const throttle = <T extends (...args: unknown[]) => void>(
  func: T,
  delay: number
): T => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return ((...args: Parameters<T>) => {
    if (timeoutId) return;

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  }) as T;
};

export const initResponsive = (): void => {
  updateRootFontSize();
  window.addEventListener('resize', throttle(updateRootFontSize, 16));
};

/**
 * Detects if the current device is a mobile device based on user agent and screen size
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const ua = navigator.userAgent;
  const isMobileUA = /Android|iPhone|iPad|iPod|Mobile|mobile/i.test(ua);
  const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;

  return isMobileUA || isSmallScreen;
};

/**
 * Detects if the user is using a mobile wallet app (not a browser extension)
 * Mobile wallets typically inject the provider differently than browser extensions
 */
export const isMobileWallet = (): boolean => {
  if (typeof window === 'undefined') return false;

  const solana = (window as any).solana;
  const phantom = (window as any).phantom?.solana;

  // Mobile detection via user agent and screen
  const isMobile = isMobileDevice();

  // Check if wallet provider exists
  const hasWalletProvider = !!(solana || phantom);

  // Browser extensions typically have the _extension property
  // Mobile wallet apps inject providers differently
  const isBrowserExtension = solana?._extension || phantom?._extension;

  return isMobile && hasWalletProvider && !isBrowserExtension;
};
